import axios from 'axios';

const API_BASE_URL = 'https://vitaldev.vitalinsights.in/admin/strength-reports';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export const strengthReportService = {
  // Get report by appointment ID
  getReportByAppointmentId: async (appointmentId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/by-appointment/${appointmentId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching strength report:', error);
      throw error;
    }
  },

  // Create new strength report
  createReport: async (payload) => {
    try {
      const response = await axios.post(
        API_BASE_URL,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating strength report:', error);
      throw error;
    }
  },

  // Update existing strength report
  updateReport: async (reportId, payload) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/${reportId}`,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating strength report:', error);
      throw error;
    }
  },

  // Generate HTML for preview/PDF
  generateStrengthReportHTML: async (formData) => {
    try {
      // Fetch the HTML template
      const templateResponse = await fetch('/templates/strength_report.html');
      let html = await templateResponse.text();

      // Helper function to calculate asymmetry
      const calculateAsymmetry = (right, left) => {
        if (!right || !left) return '';
        
        const rightVal = parseFloat(right);
        const leftVal = parseFloat(left);
        
        if (isNaN(rightVal) || isNaN(leftVal)) return '';
        
        const max = Math.max(rightVal, leftVal);
        const min = Math.min(rightVal, leftVal);
        
        if (max === 0) return '';
        
        const asymmetry = ((max - min) / max * 100).toFixed(1);
        const side = rightVal > leftVal ? 'Right' : 'Left';
        
        return `${asymmetry}% ${side}`;
      };

      // Helper function to format exercise data
      const formatExerciseData = (exercises) => {
        if (!exercises || exercises.length === 0) return '';
        
        return exercises.map((exercise, index) => {
          const asymmetry = calculateAsymmetry(exercise.right, exercise.left);
          return `
            <div class="exercise-card">
              <h3 class="exercise-title">${exercise.name || `Exercise ${index + 1}`}</h3>
              
              <div class="data-row">
                <div class="side-data right-side">
                  <span class="label">Right</span>
                  <span class="weight">${exercise.right ? exercise.right + 'KG' : '-'}</span>
                </div>
                
                <div class="side-data left-side">
                  <span class="label">Left</span>
                  <span class="weight">${exercise.left ? exercise.left + 'KG' : '-'}</span>
                </div>
              </div>
              
              <div class="asymmetry-row">
                <span class="asymmetry-label">Asymmetry</span>
                <span class="asymmetry-value">${asymmetry || '-'}</span>
              </div>
            </div>
            <hr class="card-separator">
          `;
        }).join('');
      };

      const formatExerciseData1 = (exercises, descriptions) => {
        if (!exercises || exercises.length === 0) return '';
        
        return exercises.map((exercise, index) => {
          const asymmetry = calculateAsymmetry(exercise.right, exercise.left);
          return `
              <div class="exercise-card">
                <h3 class="exercise-title">${exercise.name || `Exercise ${index + 1}`}</h3>
                <p class="exercise-description">${descriptions[index]}</p>
                
                <div class="data-row">
                    <div class="side-data right-side">
                        <span class="label">Right</span>
                        <span class="weight">${exercise.right ? exercise.right + 'KG' : '-'}</span>
                        <div class="percentage-box green">
                            <span class="arrow up"></span>
                            <span class="percent">5.3%</span>
                        </div>
                        <div class="detail-percent">13.8% Left</div>
                    </div>
                    <div class="side-data left-side">
                        <span class="label">Left</span>
                        <span class="weight">${exercise.left ? exercise.left + 'KG' : '-'}</span>
                        <div class="percentage-box pink">
                            <span class="arrow down"></span>
                            <span class="percent">5.3%</span>
                        </div>
                        <div class="detail-percent">13.8% Left</div>
                    </div>
                </div>
                
                <div class="asymmetry-row">
                    <span class="asymmetry-label">Asymmetry - Fair</span>
                    <span class="asymmetry-value orange">${asymmetry || '-'}</span>
                </div>
            </div>
            <hr class="card-separator">

          `;
        }).join('');
      };


      // Helper function to format suggestions
      const formatSuggestions = (suggestions) => {
        if (!suggestions || suggestions.length === 0) return '';
        
        return suggestions.map((suggestion, index) => `
          <div class="suggestion-card">
            <h4>${suggestion.title || `Suggestion ${index + 1}`}</h4>
            ${suggestion.description ? `<p>${suggestion.description}</p>` : ''}
          </div>
        `).join('');
      };

      let upperDes = ["Builds <b>deltoid</b> strength and improves shoulder stability.","Activates and strengthens the biceps, improving arm power.", "Strengthens the triceps to enhance arm stability.", "Strengthens the forearm muscles, improving grip power."]
      let lowerDes = ["Activates the hamstrings, calves, and supporting stabilizers to generate controlled power.","Activates the tibialis muscles, improving lower-leg strength and joint stability.", "Directly engages the quadriceps, improving strength, mobility, and lower-body control.", "Enhances calf flexibility and balance, supporting stronger, more stable movement."]
      // Replace placeholders
      html = html.replaceAll('{{patient_name}}', formData.patient_name || '');
      html = html.replaceAll('{{patient_gender}}', formData.patient_gender || '');
      html = html.replaceAll('{{patient_age}}', formData.patient_age || '');
      html = html.replaceAll('{{exam_date}}', formData.exam_date || '');
      
      // Upper body exercises
      html = html.replace('{{upper_body}}', formatExerciseData1(formData.upper_body || [], upperDes));
      
      // Lower body exercises
      html = html.replace('{{lower_body}}', formatExerciseData1(formData.lower_body || [], lowerDes));
      
      // Suggestions
      html = html.replace('{{suggestions}}', formatSuggestions(formData.suggestions || []));
      
      // Additional fields
      html = html.replace('{{notes}}', formData.notes || '');
      html = html.replace('{{recommendations}}', formData.recommendations || '');

      return html;
    } catch (error) {
      console.error('Error generating HTML:', error);
      throw error;
    }
  }
};

// Export the HTML generation function separately for convenience
export const generateStrengthReportHTML = strengthReportService.generateStrengthReportHTML;
